/**
 * Configuration.ts
 *
 * Module for logic relating to the sdk user configuration
 */

import * as Utils from "./../Utils";
import * as Constants from "./../Constants";
import * as Localization from "./../Localization";
import { ICategoryOptions } from "./ICategoryOptions";
import * as IInitOptions from "./IInitOptions";
import * as ILaunchOptions from "./ILaunchOptions";
import IUISurvey from "./../FloodgateCore/UISurvey/IUISurvey";
import { IOnSurveyActivatedCallback, ISurveyForm, ISurveyLauncher } from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import { ISurveyParams } from "@ms-ofb/officefloodgatecore/dist/src/Api/IOnSurveyActivatedCallback";
import { Urls } from "../UI/UiConstants";

export * from "./IInitOptions";
export * from "./ILaunchOptions";

/**
 * Class representing a configuration (user provided options)
 */

export class Configuration {
	/**
	 * Validate category options, default "show" to false if CategoryOptions is not provided.
	 * @param categories Category options
	 * @returns {ICategoryOptions} the validated CategoryOptions
	 */
	private static validateCategoryOptions(categories?: ICategoryOptions): ICategoryOptions {
		if (!categories) {
			return { show: false };
		}

		Utils.expectBoolean(categories.show, "categories.show");
		if (categories.customCategories) {
			Utils.expectArray(categories.customCategories, "categories.customCategories");

			for (let customCategory of categories.customCategories) {
				Utils.expectString(customCategory, "custom category '" + customCategory + "'");
				if (customCategory.length > 20) {
					throw "custom category '" + customCategory + "' too long, max 20 characters.";
				}
			}
		}

		return categories;
	}

	private commonInitOptions: IInitOptions.IInitOptionsCommon;

	private inAppFeedbackInitOptions: IInitOptions.IInitOptionsInAppFeedback;
	private inAppFeedbackLaunchOptions: ILaunchOptions.ILaunchOptionsInAppFeedback;

	private floodgateInitOptions: IInitOptions.IInitOptionsFloodgate;
	private floodgateSurvey: IUISurvey;

	private sdkSessionId: string;

	private eventSampling: IInitOptions.ISamplingInitOption;

	private commonPolicyAllowAADC: boolean;

	private feedbackSubmitted: boolean = false;

	public getFeedbackSubmitted(): boolean {
		return this.feedbackSubmitted;
	}

	public setFeedbackSubmitted(value: boolean): void {
		this.feedbackSubmitted = value;
	}

	public getCommonInitOptions(): IInitOptions.IInitOptionsCommon {
		return this.commonInitOptions;
	}

	public getInAppFeedbackInitOptions(): IInitOptions.IInitOptionsInAppFeedback {
		return this.inAppFeedbackInitOptions;
	}

	public getInAppFeedbackLaunchOptions(): ILaunchOptions.ILaunchOptionsInAppFeedback {
		return this.inAppFeedbackLaunchOptions;
	}

	public getFloodgateInitOptions(): IInitOptions.IInitOptionsFloodgate {
		return this.floodgateInitOptions;
	}

	public getFloodgateSurvey(): IUISurvey {
		return this.floodgateSurvey;
	}

	public getSdkSessionId(): string {
		if (!this.sdkSessionId) {
			this.sdkSessionId = Utils.guid();
		}

		return this.sdkSessionId;
	}

	public getEventSampling(): IInitOptions.ISamplingInitOption {
		return this.eventSampling;
	}

	/**
	 * Set initOptions for common functionality.
	 */
	public setCommonInitOptions(passedInitOptions: IInitOptions.IInitOptionsCommon) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");
		let initOptions: IInitOptions.IInitOptionsCommon = {
			appId: passedInitOptions.appId,
			applicationGroup: passedInitOptions.applicationGroup || {},
			build: passedInitOptions.build,
			cid: passedInitOptions.cid,
			environment: passedInitOptions.environment,
			intlFilename: passedInitOptions.intlFilename,
			intlUrl: passedInitOptions.intlUrl,
			webSurfacesResourceBaseUrl: passedInitOptions.webSurfacesResourceBaseUrl,
			locale: passedInitOptions.locale,
			onError: passedInitOptions.onError,
			originalLocale: passedInitOptions.locale,
			primaryColour: passedInitOptions.primaryColour,
			secondaryColour: passedInitOptions.secondaryColour,
			sessionId: passedInitOptions.sessionId,
			stylesUrl: passedInitOptions.stylesUrl,
			telemetryGroup: passedInitOptions.telemetryGroup || {},
			userEmail: passedInitOptions.userEmail,
			webGroup: passedInitOptions.webGroup || {},
			eventSampling: passedInitOptions.eventSampling,
			petrolTimeout: passedInitOptions.petrolTimeout,
			isCommercialHost: passedInitOptions.isCommercialHost,
			customResourcesSetExternally: passedInitOptions.customResourcesSetExternally,
			emailPolicyValue: passedInitOptions.emailPolicyValue,
			screenshotPolicyValue: passedInitOptions.screenshotPolicyValue,
			ageGroup: passedInitOptions.ageGroup,
			authenticationType: passedInitOptions.authenticationType,
			applicationSettings: passedInitOptions.applicationSettings,
			webSurfacesSettings: passedInitOptions.webSurfacesSettings,
			privacyUrl: passedInitOptions.privacyUrl || Urls.PrivacyStatementLink,
			retentionDurationDays: passedInitOptions.retentionDurationDays,
			telemetrySink: passedInitOptions.telemetrySink,
		};

		Utils.expectNumber(initOptions.appId, "initOptions.appId");
		if (!(initOptions.appId % 1 === 0 && initOptions.appId > 0)) {
			throw "initOptions.appId is not a positive integer: " + initOptions.appId;
		}

		// if defined, it should be a positive number
		if (initOptions.retentionDurationDays !== undefined) {
			Utils.expectNumber(initOptions.retentionDurationDays, "initOptions.retentionDurationDays");
			if (!(initOptions.retentionDurationDays % 1 === 0 && initOptions.retentionDurationDays > 0)) {
				throw "initOptions.retentionDurationDays is not a positive integer: " + initOptions.retentionDurationDays;
			}
		}

		// tslint:disable-next-line: no-bitwise
		const expectedValues = IInitOptions.CustomResources.Strings | IInitOptions.CustomResources.Css;

		if (initOptions.customResourcesSetExternally === undefined) {
			initOptions.customResourcesSetExternally = IInitOptions.CustomResources.None;
			// tslint:disable-next-line: no-bitwise
		} else if (typeof (initOptions.customResourcesSetExternally) !== "number" ||
			// tslint:disable-next-line: no-bitwise
			(initOptions.customResourcesSetExternally | expectedValues) !== expectedValues) {
			throw "initOptions.customResourcesSetExternally has an invalid value: " + initOptions.customResourcesSetExternally;
		}
		// tslint:disable-next-line: no-bitwise
		if ((initOptions.customResourcesSetExternally & IInitOptions.CustomResources.Css) !== IInitOptions.CustomResources.Css) {
			Utils.expectString(initOptions.stylesUrl, "initOptions.stylesUrl");
		}
		// tslint:disable-next-line: no-bitwise
		if ((initOptions.customResourcesSetExternally & IInitOptions.CustomResources.Strings) !== IInitOptions.CustomResources.Strings) {
			Utils.expectString(initOptions.intlUrl, "initOptions.intlUrl");
		}

		if (initOptions.intlFilename !== undefined) {
			Utils.expectString(initOptions.intlFilename, "initOptions.intlFilename");
		} else {
			initOptions.intlFilename = "officebrowserfeedbackstrings.js";
		}

		Utils.expectNumber(initOptions.environment, "initOptions.environment");
		if (!(initOptions.environment === 1 || initOptions.environment === 0)) {
			throw "initOptions.environment has a bad value: " + initOptions.environment;
		}

		if (initOptions.userEmail !== undefined) {
			Utils.expectString(initOptions.userEmail, "initOptions.userEmail");
		} else {
			initOptions.userEmail = "";
		}

		if (initOptions.sessionId !== undefined) {
			Utils.expectString(initOptions.sessionId, "initOptions.sessionId");
		} else {
			initOptions.sessionId = "00000000-0000-0000-0000-000000000000";
		}

		// Copy sessionID over to telemetryGroup
		initOptions.telemetryGroup.processSessionId = initOptions.sessionId;

		if (initOptions.cid !== undefined) {
			Utils.expectString(initOptions.cid, "initOptions.cid");

			// Copy cid over to extendedManifestData of applicationGroup
			initOptions.applicationGroup.extendedManifestData = JSON.stringify({ cid: initOptions.cid });
		}

		if (initOptions.build !== undefined) {
			Utils.expectString(initOptions.build, "initOptions.build");
			let buildRegex = "^[0-9]{1,9}(\\.[0-9]{1,9})?(\\.[0-9]{1,9})?(\\.[0-9]{1,9})?$";
			if (!new RegExp(buildRegex).test(initOptions.build)) {
				initOptions.build = "0.0.0.0";
			}
		} else {
			initOptions.build = "0.0.0.0";
		}
		// Copy build over to telemetryGroup
		initOptions.telemetryGroup.officeBuild = initOptions.build;

		let hexColourRegex = new RegExp("^#[0-9a-f]{3}([0-9a-f]{3})?$", "i");
		if (initOptions.primaryColour !== undefined) {
			if (!hexColourRegex.test(initOptions.primaryColour)) {
				initOptions.primaryColour = undefined;
			}
		}

		if (initOptions.secondaryColour !== undefined) {
			if (!hexColourRegex.test(initOptions.secondaryColour)) {
				initOptions.secondaryColour = undefined;
			}
		}

		if (initOptions.locale !== undefined) {
			Utils.expectString(initOptions.locale, "initOptions.locale");
			initOptions.locale = Localization.validate(initOptions.locale);
		} else {
			initOptions.locale = "en";
		}

		if (initOptions.onError === undefined) {
			initOptions.onError = (err: string) => { return; };
		}

		if (initOptions.petrolTimeout !== undefined) {
			Utils.expectNumber(initOptions.petrolTimeout, "initOptions.petrolTimeout");
		}

		if (initOptions.authenticationType !== undefined) {
			Utils.expectEnum(initOptions.authenticationType, IInitOptions.AuthenticationType, "initOptions.authenticationType");
		} else {
			initOptions.authenticationType = IInitOptions.AuthenticationType.Unauthenticated;
		}

		// For AAD or Unauthenticated, ageGroup value is unknown, so it's safe to only parse for MSA
		if (initOptions.ageGroup !== undefined) {
			Utils.expectEnum(initOptions.ageGroup, IInitOptions.AgeGroup, "initOptions.ageGroup");
		} else {
			initOptions.ageGroup = IInitOptions.AgeGroup.Undefined;
		}

		this.eventSampling = this.parseEventSamplingOption(initOptions.eventSampling);

		this.commonInitOptions = initOptions;
		this.commonPolicyAllowAADC = this.determineAADCPolicy(initOptions.authenticationType, initOptions.ageGroup);

		if (initOptions.isCommercialHost === undefined || initOptions.isCommercialHost == null) {
			initOptions.isCommercialHost = false; // By default privacy consent is not shown. (From SDK's perspective)
		} else {
			Utils.expectBoolean(initOptions.isCommercialHost, "initOptions.isCommercialHost");
		}

		if (initOptions.emailPolicyValue === undefined) {
			initOptions.emailPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.emailPolicyValue) !== "number" ||
			(initOptions.emailPolicyValue !== Constants.OCPSValues.NotConfigured &&
				initOptions.emailPolicyValue !== Constants.OCPSValues.Enabled &&
				initOptions.emailPolicyValue !== Constants.OCPSValues.Disabled)) {
			throw "initOptions.emailPolicyValue has an invalid value: " + initOptions.emailPolicyValue;
		}

		if (initOptions.screenshotPolicyValue === undefined) {
			initOptions.screenshotPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.screenshotPolicyValue) !== "number" ||
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.NotConfigured &&
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.Enabled &&
			initOptions.screenshotPolicyValue !== Constants.OCPSValues.Disabled) {
			throw "initOptions.screenshotPolicyValue has an invalid value: " + initOptions.screenshotPolicyValue;
		}

		if (initOptions.privacyUrl !== undefined) {
			Utils.expectString(initOptions.privacyUrl, "initOptions.privacyUrl");
		} else {
			initOptions.privacyUrl = Urls.PrivacyStatementLink;
		}

		// Expecting telemetrySink is given
		Utils.expectObject(initOptions.telemetrySink, "initOptions.telemetrySink");
	}

	/**
	 * Set init options for inAppFeedback
	 */
	public setInAppFeedbackInitOptions(passedInitOptions: IInitOptions.IInitOptionsInAppFeedback) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");

		let initOptions: IInitOptions.IInitOptionsInAppFeedback = {
			bugForm: passedInitOptions.bugForm,
			onDismiss: passedInitOptions.onDismiss,
			screenshot: passedInitOptions.screenshot,
			showEmailAddress: passedInitOptions.showEmailAddress,
			userEmail: passedInitOptions.userEmail,
			transitionEnabled: passedInitOptions.transitionEnabled,
			isShowThanks: passedInitOptions.isShowThanks,
			sendFeedbackPolicyValue: passedInitOptions.sendFeedbackPolicyValue,
			feedbackForumUrl: passedInitOptions.feedbackForumUrl !== undefined ?
				passedInitOptions.feedbackForumUrl :
				undefined,
			myFeedbackForumUrl: passedInitOptions.myFeedbackForumUrl !== undefined ?
				passedInitOptions.myFeedbackForumUrl :
				undefined,
		};

		if (initOptions.onDismiss === undefined) {
			initOptions.onDismiss = function (submitted: boolean) { return; };
		}

		if (initOptions.bugForm !== undefined) {
			Utils.expectBoolean(initOptions.bugForm, "initOptions.bugForm");
		} else {
			initOptions.bugForm = false;
		}

		if (initOptions.userEmail !== undefined) {
			Utils.expectString(initOptions.userEmail, "initOptions.userEmail");
		}

		if (initOptions.screenshot !== undefined) {
			Utils.expectBoolean(initOptions.screenshot, "initOptions.screenshot");
		} else {
			initOptions.screenshot = true;
		}

		if (initOptions.showEmailAddress !== undefined) {
			Utils.expectBoolean(initOptions.showEmailAddress, "initOptions.showEmailAddress");
		} else {
			initOptions.showEmailAddress = true;
		}

		if (initOptions.feedbackForumUrl !== undefined) {
			Utils.expectString(initOptions.feedbackForumUrl, "initOptions.feedbackForumUrl");
		} else {
			initOptions.feedbackForumUrl = undefined;
		}

		if (initOptions.isShowThanks !== undefined) {
			Utils.expectBoolean(initOptions.isShowThanks, "initOptions.isShowThanks");
		} else {
			initOptions.isShowThanks = true;
		}

		if (initOptions.myFeedbackForumUrl !== undefined) {
			Utils.expectString(initOptions.myFeedbackForumUrl, "initOptions.myFeedbackForumUrl");
		} else {
			initOptions.myFeedbackForumUrl = undefined;
		}

		if (initOptions.transitionEnabled === undefined) {
			initOptions.transitionEnabled = true;
		}

		if (initOptions.sendFeedbackPolicyValue === undefined) {
			initOptions.sendFeedbackPolicyValue = Constants.OCPSValues.NotConfigured;
		} else if (typeof (initOptions.sendFeedbackPolicyValue) !== "number" ||
			(initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.NotConfigured &&
				initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.Enabled &&
				initOptions.sendFeedbackPolicyValue !== Constants.OCPSValues.Disabled)) {
			throw "initOptions.sendFeedbackPolicyValue has an invalid value: " + initOptions.sendFeedbackPolicyValue;
		}

		this.inAppFeedbackInitOptions = initOptions;
	}

	/**
	 * Set launch options for inAppFeedback
	 */
	public setInAppFeedbackLaunchOptions(launchOptions?: ILaunchOptions.ILaunchOptionsInAppFeedback) {
		if (!this.commonInitOptions) {
			throw "commonInitOptions not set";
		}

		if (!launchOptions) {
			launchOptions = {};
		}

		// Override any setting done in LaunchOptions for the metadataGroups
		this.commonInitOptions.applicationGroup = Utils.overrideValues(launchOptions.applicationGroup,
			this.commonInitOptions.applicationGroup);
		this.commonInitOptions.telemetryGroup = Utils.overrideValues(launchOptions.telemetryGroup,
			this.commonInitOptions.telemetryGroup);
		this.commonInitOptions.webGroup = Utils.overrideValues(launchOptions.webGroup,
			this.commonInitOptions.webGroup);

		launchOptions.categories = Configuration.validateCategoryOptions(launchOptions.categories);

		this.inAppFeedbackLaunchOptions = launchOptions;
	}

	/**
	 * Set init options for floodgate
	 */
	public setFloodgateInitOptions(passedInitOptions: IInitOptions.IInitOptionsFloodgate) {
		Utils.expectObject(passedInitOptions, "passedInitOptions");

		let initOptions: IInitOptions.IInitOptionsFloodgate = {
			autoDismiss: passedInitOptions.autoDismiss,
			campaignDefinitions: passedInitOptions.campaignDefinitions,
			campaignFlights: passedInitOptions.campaignFlights,
			campaignQueryParameters: passedInitOptions.campaignQueryParameters,
			onDismiss: passedInitOptions.onDismiss,
			onSurveyActivatedCallback: passedInitOptions.onSurveyActivatedCallback,
			settingStorageCallback: passedInitOptions.settingStorageCallback,
			uIStringGetter: passedInitOptions.uIStringGetter,
			authTokenCallback: passedInitOptions.authTokenCallback,
			surveyEnabled: passedInitOptions.surveyEnabled,
			showEmailAddress: passedInitOptions.showEmailAddress,
			augLoopCallback: passedInitOptions.augLoopCallback,
			customerVoiceSurveyParams: passedInitOptions.customerVoiceSurveyParams,
			surveyCSSParams: passedInitOptions.surveyCSSParams,
			governanceServiceEnabled: passedInitOptions.governanceServiceEnabled,
			governanceServiceConfig: passedInitOptions.governanceServiceConfig,
			personalizerEnabled: passedInitOptions.personalizerEnabled,
			messagingServiceSettings: passedInitOptions.messagingServiceSettings,
			isPriorityEnabled: passedInitOptions.isPriorityEnabled,
			isEudbUxEnabled: passedInitOptions.isEudbUxEnabled,
		};

		if (initOptions.autoDismiss === undefined) {
			initOptions.autoDismiss = Constants.AutoDismissValues.NoAutoDismiss;
		}

		if (initOptions.onDismiss === undefined) {
			initOptions.onDismiss = (campaignId: string, submitted: boolean) => { return; };
		}

		if (initOptions.onSurveyActivatedCallback === undefined) {
			let defaultOnSurveyActivatedCallback: IOnSurveyActivatedCallback = {
				onSurveyActivated(launcher: ISurveyLauncher, survey?: ISurveyForm, surveyParams?: ISurveyParams): void {
					launcher.launch();
				},
			};

			initOptions.onSurveyActivatedCallback = defaultOnSurveyActivatedCallback;
		}

		if (initOptions.uIStringGetter === undefined) {
			initOptions.uIStringGetter = (str: string) => str; // By default return the string as is.
		}

		if (initOptions.surveyEnabled === undefined) {
			initOptions.surveyEnabled = true; // By default surveys are enabled. (From SDK's perspective)
		} else {
			Utils.expectBoolean(initOptions.surveyEnabled, "surveyEnabled");
		}

		if (initOptions.showEmailAddress !== undefined) {
			Utils.expectBoolean(initOptions.showEmailAddress, "initOptionsFloodgate.showEmailAddress");
		} else {
			initOptions.showEmailAddress = true;  // By default email field and collection is enabled. (From SDK's perspective)
		}

		if (initOptions.governanceServiceEnabled !== undefined) {
			Utils.expectBoolean(initOptions.governanceServiceEnabled, "initOptionsFloodgate.governanceServiceEnable");
		} else {
			initOptions.governanceServiceEnabled = false;  // By default governanceServiceEnable is disabled
		}

		if (initOptions.governanceServiceEnabled) {
			initOptions.governanceServiceConfig = this.parseGovernanceServiceConfig(
				initOptions.governanceServiceConfig !== undefined ?
					initOptions.governanceServiceConfig :
					{});
		}

		if (initOptions.customerVoiceSurveyParams === undefined) {
			initOptions.customerVoiceSurveyParams = { cvFlights: "", isCVSurveyEnabled: true, productName: "", uiHost: "" };
		} else {
			if (Utils.isNOU(initOptions.customerVoiceSurveyParams.isCVSurveyEnabled)) {
				initOptions.customerVoiceSurveyParams.isCVSurveyEnabled = true;
			}
		}

		if (initOptions.personalizerEnabled !== undefined) {
			Utils.expectBoolean(initOptions.personalizerEnabled, "initOptionsFloodgate.personalizerEnabled");
		} else {
			initOptions.personalizerEnabled = false;  // By default personalizerEnabled is disabled
		}

		if (initOptions.isPriorityEnabled !== undefined) {
			Utils.expectBoolean(initOptions.isPriorityEnabled, "initOptionsFloodgate.isPriorityEnabled");
		} else {
			initOptions.isPriorityEnabled = true;  // By default isPriorityEnabled is enabled
		}

		if (initOptions.isEudbUxEnabled !== undefined) {
			Utils.expectBoolean(initOptions.isEudbUxEnabled, "initOptionsFloodgate.isEudbUxEnabled");
		} else {
			initOptions.isEudbUxEnabled = true;  // By default isEudbUxEnabled is enabled
		}

		this.floodgateInitOptions = initOptions;
	}

	/**
	 * Set the floodgate survey
	 */
	public setFloodgateSurvey(survey: IUISurvey): void {
		this.floodgateSurvey = survey;
	}

	// Checks if the surveyEnabled policy is true or false.
	public checkIfSurveysEnabled(): boolean {
		// If no floodgateInitOptions defined, return true for surveyEnabled
		const policyAllowSurvey = this.floodgateInitOptions ? this.floodgateInitOptions.surveyEnabled : true;
		return policyAllowSurvey && this.commonPolicyAllowAADC;
	}

	/**
	 * Return the flag the Feedback and Survey enabled by AADC.
	 */
	public isFeedbackSurveyEnabledByAADC(): boolean {
		return this.commonPolicyAllowAADC;
	}

	public getCVSurveyFlights(): string | null {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.cvFlights : "";
	}

	public isCVSurveyEnabled(): boolean {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.isCVSurveyEnabled : true;
	}

	public getProductName(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.productName : "";
	}

	public getUIHost(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.uiHost : "";
	}

	public getUIHostCVSurveyDispatcher(): string {
		return this.floodgateInitOptions && this.floodgateInitOptions.customerVoiceSurveyParams ?
			this.floodgateInitOptions.customerVoiceSurveyParams.uiHostCVSurveyDispatcher : "";
	}

	public getCSSPosition(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssPosition ?
			this.floodgateInitOptions.surveyCSSParams.cssPosition :
			"";
	}

	public getCSSZIndex(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssZIndex ?
			this.floodgateInitOptions.surveyCSSParams.cssZIndex :
			"";
	}

	public getCSSBottom(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssBottom ?
			this.floodgateInitOptions.surveyCSSParams.cssBottom :
			"";
	}

	public getCSSRight(): string {
		return this.floodgateInitOptions &&
			this.floodgateInitOptions.surveyCSSParams &&
			this.floodgateInitOptions.surveyCSSParams.cssRight ?
			this.floodgateInitOptions.surveyCSSParams.cssRight :
			"";
	}

	public getCSSVarString(): string {
		const styleSubstrings: string[] = [];
		if (this.floodgateInitOptions && this.floodgateInitOptions.surveyCSSParams) {
			if (this.floodgateInitOptions.surveyCSSParams.cssPosition) {
				styleSubstrings.push("--obf-position:", this.floodgateInitOptions.surveyCSSParams.cssPosition, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssZIndex) {
				styleSubstrings.push("--obf-zIndex:", this.floodgateInitOptions.surveyCSSParams.cssZIndex, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssBottom) {
				styleSubstrings.push("--obf-bottom:", this.floodgateInitOptions.surveyCSSParams.cssBottom, ";");
			}
			if (this.floodgateInitOptions.surveyCSSParams.cssRight) {
				styleSubstrings.push("--obf-right:", this.floodgateInitOptions.surveyCSSParams.cssRight, ";");
			}
		}
		return styleSubstrings.join("");
	}

	/**
	 * Check AADC rule at once since Configuration cannot be set again.
	 * So that we don't need to repeat the same process over again.
	 */
	public determineAADCPolicy(authenticationType: IInitOptions.AuthenticationType,
		ageGroup: IInitOptions.AgeGroup): boolean {
		// Return Based on authentication first
		if (authenticationType === IInitOptions.AuthenticationType.Unauthenticated ||
			authenticationType === IInitOptions.AuthenticationType.AAD) {
			return true;
		}
		// Return based on AgeGroup
		return ageGroup !== IInitOptions.AgeGroup.MinorWithParentalConsent &&
			ageGroup !== IInitOptions.AgeGroup.MinorWithoutParentalConsent;
	}

	// Keep implementation in sync with %SRCROOT%\privacy\idconsentprovider\IdentityBasedConsentGroupProvider.cpp
	public getUserConsentGroup(): IInitOptions.UserConsentGroup {
		const authenticationType = this.commonInitOptions.authenticationType;
		if (authenticationType === IInitOptions.AuthenticationType.Unauthenticated ||
			authenticationType === IInitOptions.AuthenticationType.AAD) {
			return IInitOptions.UserConsentGroup.CanConsent;
		}

		const ageGroup = this.commonInitOptions.ageGroup;
		switch (ageGroup) {
			case IInitOptions.AgeGroup.Undefined:
				return IInitOptions.UserConsentGroup.CanConsent;
			case IInitOptions.AgeGroup.MinorWithoutParentalConsent:
				return IInitOptions.UserConsentGroup.CannotConsent;
			case IInitOptions.AgeGroup.MinorWithParentalConsent:
				return IInitOptions.UserConsentGroup.CannotConsent;
			case IInitOptions.AgeGroup.Adult:
				return IInitOptions.UserConsentGroup.CanConsent;
			case IInitOptions.AgeGroup.NotAdult:
				return IInitOptions.UserConsentGroup.PartialConsent;
			case IInitOptions.AgeGroup.MinorNoParentalConsentRequired:
				return IInitOptions.UserConsentGroup.CanConsent;
			default:
				return IInitOptions.UserConsentGroup.CannotConsent;
		}

		return IInitOptions.UserConsentGroup.CannotConsent;
	}

	private parseEventSamplingOption(samplingOptions: IInitOptions.ISamplingEvent[]): IInitOptions.ISamplingInitOption {
		let eventInitSampling: IInitOptions.ISamplingInitOption = { event: {} };
		if (samplingOptions !== undefined) {
			// Iterating through init options
			for (const index in samplingOptions) {
				if (samplingOptions.hasOwnProperty(index)) {
					// Check for correct Type
					switch (samplingOptions[index].type) {
						case "Event":
							// Parsing the Name and SampleRate
							let sampleRate = samplingOptions[index].sampleRate;
							Utils.expectNumber(sampleRate, "initOption.EventSampling.SampleRate");
							Utils.expectString(samplingOptions[index].name, "initOption.EventSampling.Name");
							// check the range for sampling percentage
							if (sampleRate < 0 || sampleRate > 1) {
								throw "initOption.EventSampling." + samplingOptions[index].name + ".SampleRate is out of range";
							}
							// finally, save the sampling event
							eventInitSampling.event[samplingOptions[index].name] = sampleRate;
							break;
						default:
							throw "initOption.EventSampling.Type value is not recognized";
					}
				}
			}
		}
		return eventInitSampling;
	}

	private parseGovernanceServiceConfig(config: IInitOptions.IGovernanceServiceConfig): IInitOptions.IGovernanceServiceConfig {
		return {
			// by default, disable the trigger in failure
			forceServicelessSurveyDisplay: config.forceServicelessSurveyDisplay ?? false,

			// by default, no retry
			retry: config.retry ?? 1,

			// by default, timeout is 1000 miliseconds
			timeout: config.timeout ?? 1000,

			// simple request is default to false
			simpleRequestEnabled: config.simpleRequestEnabled ?? false,
		};
	}
}

let configuration: Configuration = new Configuration();

/**
 * Get the current configuration
 */
export function get(): Configuration {
	return configuration;
}

/**
 * Validate feedbackType
 * @param feedbackType feedbackType
 * @returns {Constants.FeedbackType} the validated feedbackType
 */
export function validateFeedbackType(feedbackType: string): Constants.FeedbackType {
	Utils.expectString(feedbackType, "feedbackType");

	if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Smile]) {
		return Constants.FeedbackType.Smile;
	} else if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Frown]) {
		return Constants.FeedbackType.Frown;
	} else if (feedbackType === Constants.FeedbackType[Constants.FeedbackType.Bug]) {
		return Constants.FeedbackType.Bug;
	} else {
		throw "feedbackType should be one of Smile, Frown or Bug";
	}
}
